"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { useMotion } from "@/contexts/MotionContext";
import type { ServiceWorkSlide } from "@/lib/content/work-carousel";

const ROTATE_MS = 4500;

export default function ServiceHeroMediaCarousel({
  slides,
  className = "",
}: {
  slides: ServiceWorkSlide[];
  className?: string;
}) {
  const { isMotionEnabled } = useMotion();
  const [index, setIndex] = useState(0);

  const safeSlides = slides.length > 0 ? slides : [];

  useEffect(() => {
    if (!isMotionEnabled || safeSlides.length <= 1) {
      setIndex(0);
      return;
    }
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % safeSlides.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [isMotionEnabled, safeSlides.length]);

  const current = safeSlides[index] ?? safeSlides[0];

  if (!current) {
    return (
      <div
        className={`relative aspect-video w-full bg-background-light dark:bg-background-dark ${className}`}
      />
    );
  }

  return (
    <div
      className={`relative isolate aspect-video w-full overflow-hidden bg-background-light dark:bg-background-dark ${className}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${current.url}-${index}`}
          className="absolute inset-0"
          initial={isMotionEnabled ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          exit={isMotionEnabled ? { opacity: 0 } : { opacity: 1 }}
          transition={{ duration: isMotionEnabled ? 0.55 : 0 }}
        >
          {current.mediaType === "video" ? (
            <SlideVideo
              src={current.url}
              alt={current.alt}
              playing={isMotionEnabled}
            />
          ) : (
            <Image
              src={current.url}
              alt={current.alt}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 33vw"
            />
          )}
        </motion.div>
      </AnimatePresence>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-24 bg-gradient-to-t from-black/35 to-transparent dark:from-black/55"
        aria-hidden
      />
    </div>
  );
}

function SlideVideo({
  src,
  alt,
  playing,
}: {
  src: string;
  alt: string;
  playing: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (playing) {
      void el.play().catch(() => {});
    } else {
      el.pause();
      el.currentTime = 0;
    }
  }, [playing, src]);

  return (
    <video
      ref={ref}
      src={src}
      className="absolute inset-0 h-full w-full object-cover"
      muted
      loop
      playsInline
      aria-label={alt}
    />
  );
}
